/**
 * Category Product Filter – AJAX, no page refresh.
 * Click a category -> fetch /collections/<handle>?section_id=cpf-products -> replace product grid
 * Sort / Load more / Page numbers -> same request with &sort_by= / &page=
 * Hide sold out -> adds &filter.v.availability=1 (Shopify storefront filter) + removes any leftovers.
 * URL is updated with ?category=<handle>&sort=<sort>&page=<n> so views can be shared / Back works.
 */
(() => {
  const SECTION = 'cpf-products';

  class CPF {
    constructor(root) {
      this.root = root;
      this.base = root.dataset.root || '';
      this.startHandle = root.dataset.allHandle;
      this.paging = root.dataset.paging || 'load_more'; // 'load_more' | 'pages'
      this.hideSoldOut = root.dataset.hideSoldOut === 'true';
      this.$results = root.querySelector('[data-cpf-results]');
      this.$more = root.querySelector('[data-cpf-more]');
      this.$count = root.querySelector('[data-cpf-count]');
      this.$current = root.querySelector('[data-cpf-current]');
      this.$sort = root.querySelector('[data-cpf-sort]');
      this.$loader = root.querySelector('[data-cpf-loader]');
      this.$tags = root.querySelector('[data-cpf-tags]');
      this.countLabel = this.$count?.dataset.label || '';
      this.cache = new Map();
      this.controller = null;
      this.state = { handle: this.startHandle, sort: this.$sort?.value || '', page: 1, tag: '' };
      this.renderTags();

      root.addEventListener('click', (e) => this.onClick(e));
      this.$sort?.addEventListener('change', () => {
        this.state.sort = this.$sort.value;
        this.state.page = 1;
        this.load({ mode: 'replace', push: true });
      });
      root.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-cpf-tag-select]');
        if (sel) this.setTag(sel.value);
      });
      root.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-cpf-add]');
        if (!form) return;
        e.preventDefault();
        this.addToCart(form);
      });
      window.addEventListener('popstate', () => this.applyUrl());

      const q = new URLSearchParams(location.search);
      if (q.get('category') || q.get('sort') || q.get('page') || q.get('tag') || this.hideSoldOut) {
        // Deep link, or sold-out hiding needs the filtered count/pages from Shopify
        this.applyUrl();
      } else {
        this.sync();
      }
    }

    onClick(e) {
      const cat = e.target.closest('[data-cpf-cat]');
      if (cat) {
        e.preventDefault();
        if (cat.dataset.cpfCat === this.state.handle) return;
        this.state.handle = cat.dataset.cpfCat;
        this.state.page = 1;
        this.state.tag = '';
        this.setActive();
        this.renderTags();
        this.load({ mode: 'replace', push: true });
        return;
      }
      const pill = e.target.closest('[data-cpf-tag]');
      if (pill) {
        e.preventDefault();
        this.setTag(pill.dataset.cpfTag);
        return;
      }
      if (e.target.closest('[data-cpf-more]')) {
        e.preventDefault();
        this.state.page += 1;
        this.load({ mode: 'append', push: false });
        return;
      }
      const pageLink = e.target.closest('[data-cpf-page]');
      if (pageLink) {
        e.preventDefault();
        this.state.page = parseInt(pageLink.dataset.cpfPage, 10) || 1;
        this.load({ mode: 'replace', push: true, scroll: true });
      }
    }

    applyUrl() {
      const q = new URLSearchParams(location.search);
      const handle = q.get('category');
      const known = handle && this.root.querySelector(`[data-cpf-cat="${CSS.escape(handle)}"]`);
      this.state.handle = known ? handle : this.startHandle;
      this.state.sort = q.get('sort') || this.$sort?.value || '';
      if (this.$sort && q.get('sort')) this.$sort.value = q.get('sort');
      this.state.page = this.paging === 'pages' ? Math.max(1, parseInt(q.get('page'), 10) || 1) : 1;
      this.state.tag = q.get('tag') || '';
      this.setActive();
      this.renderTags();
      this.load({ mode: 'replace', push: false });
    }

    tagsFor(handle) {
      const el = this.root.querySelector(`[data-cpf-tags-for="${CSS.escape(handle)}"]`);
      if (!el) return [];
      let list = [];
      try { list = JSON.parse(el.textContent || '[]'); } catch { list = []; }
      if (!Array.isArray(list)) list = [];
      const seen = new Set();
      return list.map((t) => String(t).trim()).filter((t) => {
        const k = t.toLowerCase();
        if (!t || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    }

    renderTags() {
      if (!this.$tags) return;
      const tags = this.tagsFor(this.state.handle);
      if (this.state.tag && !tags.some((t) => t.toLowerCase() === this.state.tag.toLowerCase())) this.state.tag = '';
      this.$tags.hidden = tags.length === 0;
      if (!tags.length) { this.$tags.replaceChildren(); return; }

      const { style, label, allLabel } = this.$tags.dataset;
      const esc = (t) => t.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const cur = this.state.tag.toLowerCase();

      if (style === 'pills') {
        this.$tags.innerHTML =
          (label ? `<span class="cpf__tags-label">${esc(label)}</span>` : '') +
          `<button type="button" class="cpf__tag${cur ? '' : ' is-active'}" data-cpf-tag="" aria-pressed="${!cur}">${esc(allLabel || 'All')}</button>` +
          tags.map((t) => {
            const on = t.toLowerCase() === cur;
            return `<button type="button" class="cpf__tag${on ? ' is-active' : ''}" data-cpf-tag="${esc(t)}" aria-pressed="${on}">${esc(t)}</button>`;
          }).join('');
      } else {
        this.$tags.innerHTML =
          `<label class="cpf__tags-select">${label ? `<span>${esc(label)}</span>` : ''}` +
          `<select data-cpf-tag-select><option value="">${esc(allLabel || 'All')}</option>` +
          tags.map((t) => `<option value="${esc(t)}"${t.toLowerCase() === cur ? ' selected' : ''}>${esc(t)}</option>`).join('') +
          `</select></label>`;
      }
    }

    setTag(tag) {
      if ((tag || '') === this.state.tag) return;
      this.state.tag = tag || '';
      this.state.page = 1;
      this.renderTags();
      this.load({ mode: 'replace', push: true });
    }

    setActive() {
      this.root.querySelectorAll('[data-cpf-cat]').forEach((a) => {
        const on = a.dataset.cpfCat === this.state.handle;
        a.classList.toggle('is-active', on);
        a.setAttribute('aria-pressed', on);
        if (on) a.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      });
    }

    buildUrl() {
      const u = new URL(`${this.base}/collections/${this.state.handle}`, location.origin);
      u.searchParams.set('section_id', SECTION);
      if (this.state.page > 1) u.searchParams.set('page', this.state.page);
      if (this.state.sort) u.searchParams.set('sort_by', this.state.sort);
      if (this.hideSoldOut) u.searchParams.set('filter.v.availability', '1');
      if (this.state.tag) u.searchParams.set('filter.p.tag', this.state.tag);
      return u.toString();
    }

    pushUrl() {
      const q = new URLSearchParams(location.search);
      if (this.state.handle === this.startHandle) q.delete('category'); else q.set('category', this.state.handle);
      if (this.state.sort) q.set('sort', this.state.sort); else q.delete('sort');
      if (this.state.tag) q.set('tag', this.state.tag); else q.delete('tag');
      if (this.paging === 'pages' && this.state.page > 1) q.set('page', this.state.page); else q.delete('page');
      const qs = q.toString();
      history.pushState({ cpf: true }, '', location.pathname + (qs ? '?' + qs : ''));
    }

    async load({ mode, push, scroll }) {
      const url = this.buildUrl();
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setLoading(true, mode === 'append');

      try {
        let html = this.cache.get(url);
        if (!html) {
          const res = await fetch(url, { signal: this.controller.signal });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          html = await res.text();
          this.cache.set(url, html);
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const payload = doc.querySelector('[data-cpf-payload]');
        if (!payload) throw new Error('No payload');

        if (mode === 'append') {
          const grid = this.$results.querySelector('[data-cpf-grid]');
          grid.append(...payload.querySelectorAll('[data-cpf-grid] > li'));
          Object.assign(this.$results.querySelector('[data-cpf-payload]').dataset, {
            next: payload.dataset.next, page: payload.dataset.page,
          });
        } else {
          this.$results.replaceChildren(payload);
        }
        this.sync();
        if (push) this.pushUrl();
        if (scroll) this.root.querySelector('.cpf__toolbar').scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[cpf]', err);
        if (mode === 'append') this.state.page -= 1;
        this.$results.querySelector('.cpf__error')?.remove();
        this.$results.insertAdjacentHTML('afterbegin', '<p class="cpf__error">Could not load products. Please try again.</p>');
      } finally {
        this.setLoading(false);
      }
    }

    sync() {
      const p = this.$results.querySelector('[data-cpf-payload]');
      if (!p) return;

      // Fallback for stores where the "Availability" filter isn't enabled: drop sold-out cards
      const tagKey = this.state.tag ? `|${this.state.tag.toLowerCase()}|` : '';
      if (this.hideSoldOut || tagKey) {
        p.querySelectorAll('[data-cpf-grid] > li').forEach((li) => {
          if (this.hideSoldOut && li.dataset.available === 'false') li.remove();
          else if (tagKey && !(li.dataset.tags || '').includes(tagKey)) li.remove();
        });
        const grid = p.querySelector('[data-cpf-grid]');
        if (grid && !grid.children.length && !p.querySelector('.cpf__empty')) {
          grid.insertAdjacentHTML('afterend', '<p class="cpf__empty">No products available in this category right now.</p>');
        }
      }

      if (this.$count) this.$count.textContent = `${p.dataset.count} ${this.countLabel}`.trim();
      if (this.$current) {
        const active = this.root.querySelector('[data-cpf-cat].is-active');
        this.$current.textContent = active?.dataset.cpfTitle || p.dataset.title;
      }
      if (this.$more) this.$more.hidden = this.paging !== 'load_more' || !p.dataset.next;
      this.$results.classList.remove('is-pending');
    }

    setLoading(on, append = false) {
      this.root.classList.toggle('is-loading', on && !append);
      if (this.$loader) this.$loader.hidden = !on;
      if (this.$more) {
        this.$more.disabled = on;
        this.$more.classList.toggle('is-loading', on && append);
      }
    }

    async addToCart(form) {
      const btn = form.querySelector('button[type="submit"]');
      const label = btn?.textContent;
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      try {
        const res = await fetch(`${this.base}/cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!res.ok) throw new Error();
        if (btn) btn.textContent = 'Added ✓';
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        fetch(`${this.base}/cart.js`).then((r) => r.json()).then((cart) => {
          document.querySelectorAll('.cart-count-bubble span:first-child, [data-cart-count]')
            .forEach((el) => (el.textContent = cart.item_count));
        });
      } catch {
        form.submit();
        return;
      }
      setTimeout(() => { if (btn) { btn.disabled = false; btn.textContent = label; } }, 1500);
    }
  }

  const init = (scope = document) =>
    scope.querySelectorAll('[data-cpf]').forEach((el) => { if (!el.__cpf) el.__cpf = new CPF(el); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();